import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Row, Spinner, InputGroup } from 'react-bootstrap';
import { BsShieldLock, BsDiagram3, BsTelephone } from 'react-icons/bs';
import { toast } from 'react-toastify';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { callCenterTeamSettingsApi } from '@/_services/api/callCenter/callCenterTeamSettings';
import ChannelSelectFilter from '@/components/Admin/ChannelSelectFilter';
import OurAccordion from '@/_elements/OurAccordion';
import { Switch } from '@/_elements/FormElements';
import { ConsentToolTip } from '@/_elements/ToolTip';
import Select from 'react-select';
import { fetchWrapper } from '@/_helpers';
import './SettingsTab.scss';

const inboundRoutingOptions = [
    { value: 'team_queue', label: 'Team queue' },
    { value: 'fallback_user', label: 'Fallback user' },
    { value: 'voicemail', label: 'Voicemail' },
    { value: 'ivr', label: 'IVR (press digits)' },
];
const ivrDigitOptions = Array.from({ length: 9 }, (_, index) => {
    const digit = String(index + 1);
    return { value: digit, label: digit };
});
const selectMenuStyles = {
    menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const emptyRouting = {
    strategy: 'voicemail',
    fallbackUser: '',
    queueChannelId: '',
    voicemailGreetingText: '',
    recordCalls: true,
    voicemailChannelId: '',
    ivrPromptScript: '',
    ivrPromptAudioUrl: '',
    ivrVoiceId: '',
    ivrTimeoutSeconds: 6,
    ivrExtensions: [],
    numberRestrictions: [],
};

const normalizeSettingsForm = (settingsArg) => {
    const settings = settingsArg || {};
    const inboundRouting = { ...(settings.inboundRouting || {}) };
    delete inboundRouting.whisperMessage;
    delete inboundRouting.voicemailGreeting;
    delete inboundRouting.ivrMaxDigits;

    return {
        isCallingEnabled: Boolean(settings.isCallingEnabled),
        outboundCallerId: settings.outboundCallerId || '',
        inboundRouting: {
            ...emptyRouting,
            ...inboundRouting,
            recordCalls: true,
            strategy: settings.inboundRouting?.strategy || 'voicemail',
            ivrVoiceId: '',
            fallbackUser: settings.inboundRouting?.fallbackUser || '',
            numberRestrictions: Array.isArray(settings.inboundRouting?.numberRestrictions)
                ? settings.inboundRouting.numberRestrictions
                : [],
        },
    };
};

const prepareSettingsPayload = (form) => {
    const routing = form.inboundRouting;
    return {
        isCallingEnabled: form.isCallingEnabled,
        outboundCallerId: form.outboundCallerId || null,
        inboundRouting: {
            strategy: routing.strategy,
            fallbackUser: routing.fallbackUser || null,
            queueChannelId: routing.queueChannelId || null,
            voicemailGreetingText: routing.voicemailGreetingText || '',
            recordCalls: true,
            voicemailChannelId: routing.voicemailChannelId || '',
            voicemailMaxSeconds: routing.voicemailMaxSeconds,
            ivrExtensions: (routing.ivrExtensions || []).map(({ digit, room, label }) => ({ digit, room, label })),
            ivrPromptScript: routing.ivrPromptScript || '',
            ivrPromptAudioUrl: routing.ivrPromptAudioUrl || '',
            ivrPromptHash: routing.ivrPromptHash || '',
            ivrVoiceId: routing.ivrVoiceId || '',
            ivrTimeoutSeconds: routing.ivrTimeoutSeconds,
            numberRestrictions: (routing.numberRestrictions || [])
                .filter((rule) => rule.pattern)
                .map(({ pattern, permissionLevel, action, note }) => ({
                    pattern,
                    permissionLevel,
                    action,
                    note,
                })),
        },
    };
};

const getAvailableNumberValue = (number) => number?.phoneNumber || number?.phone_number || number?.e164 || '';
const getAvailableNumberLabel = (number) => number?.friendlyName || number?.friendly_name || 'Available number';

const SettingsTabComponent = ({ teamId }) => {
    const [teamSettings, setTeamSettings] = useState(null);
    const [form, setForm] = useState(normalizeSettingsForm(null));
    const [saved, setSaved] = useState(false);
    const [localError, setLocalError] = useState('');
    const [teamSettingsLoading, setTeamSettingsLoading] = useState(false);
    const [teamSettingsSaving, setTeamSettingsSaving] = useState(false);
    const [setupLoading, setSetupLoading] = useState(false);
    const [numbersLoading, setNumbersLoading] = useState(false);
    const [availableNumbers, setAvailableNumbers] = useState([]);
    const [teamUsers, setTeamUsers] = useState([]);
    const [teamUsersLoading, setTeamUsersLoading] = useState(false);
    const [areaCode, setAreaCode] = useState('');
    const [provisioning, setProvisioning] = useState('');
    const [regenLoading, setRegenLoading] = useState(false);
    const isMountedRef = useRef(true);
    const settingsRequestIdRef = useRef(0);

    const loadTeamSettings = useCallback(async () => {
        if (!teamId) {
            setTeamSettings(null);
            setForm(normalizeSettingsForm(null));
            setTeamSettingsLoading(false);
            return;
        }

        const requestId = ++settingsRequestIdRef.current;
        setTeamSettingsLoading(true);
        setLocalError('');
        try {
            const response = await callCenterTeamSettingsApi.getSettings(teamId);
            if (!isMountedRef.current || requestId !== settingsRequestIdRef.current) return;
            setTeamSettings(response || null);
            setForm(normalizeSettingsForm(response));
        } catch (err) {
            if (!isMountedRef.current || requestId !== settingsRequestIdRef.current) return;
            setTeamSettings(null);
            setForm(normalizeSettingsForm(null));
            setLocalError(err?.message || 'Unable to load call center settings');
        } finally {
            if (isMountedRef.current && requestId === settingsRequestIdRef.current) {
                setTeamSettingsLoading(false);
            }
        }
    }, [teamId]);

    useEffect(() => {
        isMountedRef.current = true;
        loadTeamSettings();
        return () => {
            isMountedRef.current = false;
        };
    }, [loadTeamSettings]);

    useEffect(() => {
        let isActive = true;
        if (!teamId) {
            setTeamUsers([]);
            setTeamUsersLoading(false);
            return undefined;
        }

        setTeamUsersLoading(true);
        fetchWrapper
            .get(`users?team=${encodeURIComponent(teamId)}&limit=1000&page=1&sortBy=name:asc`)
            .then((response) => {
                if (isActive) setTeamUsers(Array.isArray(response?.results) ? response.results : []);
            })
            .catch(() => {
                if (isActive) setTeamUsers([]);
            })
            .finally(() => {
                if (isActive) setTeamUsersLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [teamId]);

    const assignedNumbers = useMemo(
        () => (Array.isArray(teamSettings?.assignedNumbers) ? teamSettings.assignedNumbers : []),
        [teamSettings]
    );
    const outboundCallerIdOptions = useMemo(
        () =>
            assignedNumbers.filter(
                (number) => number.capabilities?.voice && (!number.status || number.status === 'active')
            ),
        [assignedNumbers]
    );
    const outboundCallerIdSelectOptions = useMemo(
        () =>
            outboundCallerIdOptions.map((number) => ({
                value: number.e164,
                label: number.e164,
            })),
        [outboundCallerIdOptions]
    );
    const fallbackUserOptions = useMemo(
        () =>
            teamUsers.map((user) => ({
                value: user.id || user._id,
                label: `${user.name || 'Team member'}${user.phone ? ` · ${user.phone}` : ' · no phone number'}`,
                isDisabled: !user.phone,
            })),
        [teamUsers]
    );
    const hasSubAccount = Boolean(teamSettings?.providerMeta?.subAccountSid);

    const handleSetupTeamCalling = async () => {
        if (!teamId) return;
        setLocalError('');
        setSetupLoading(true);
        try {
            await callCenterTeamSettingsApi.setupTeamCalling(teamId);
            await loadTeamSettings();
        } catch (err) {
            setLocalError(err?.message || 'Unable to set up team calling');
        } finally {
            setSetupLoading(false);
        }
    };

    const handleSearchNumbers = async () => {
        if (!teamId) return;
        setNumbersLoading(true);
        setLocalError('');
        setAvailableNumbers([]);
        try {
            const res = await callCenterTeamSettingsApi.getAvailableNumbers({
                team: teamId,
                areaCode: areaCode?.trim() || undefined,
            });
            const list = Array.isArray(res?.available_phone_numbers) ? res.available_phone_numbers : [];
            setAvailableNumbers(list);
        } catch (err) {
            setLocalError(err?.message || 'Unable to search numbers');
        } finally {
            setNumbersLoading(false);
        }
    };

    const handleProvision = async (num) => {
        if (!teamId || !num) return;
        const phoneNumber = getAvailableNumberValue(num);
        if (!phoneNumber) return;
        setProvisioning(phoneNumber);
        setLocalError('');
        try {
            await callCenterTeamSettingsApi.provisionNumber(teamId, { phoneNumber });
            await loadTeamSettings();
            setAvailableNumbers([]);
            setAreaCode('');
        } catch (err) {
            setLocalError(err?.message || 'Unable to provision number');
        } finally {
            setProvisioning('');
        }
    };

    const updateRouting = (key, value) => {
        setForm((prev) => ({
            ...prev,
            inboundRouting: {
                ...prev.inboundRouting,
                [key]: value,
            },
        }));
    };

    const addIvrExtension = () => {
        setForm((prev) => {
            const extensions = prev.inboundRouting.ivrExtensions || [];
            const usedDigits = new Set(extensions.map((extension) => String(extension.digit)));
            const nextDigit = ivrDigitOptions.find((option) => !usedDigits.has(option.value))?.value;

            if (!nextDigit) return prev;

            return {
                ...prev,
                inboundRouting: {
                    ...prev.inboundRouting,
                    ivrExtensions: [...extensions, { digit: nextDigit, room: '', label: '' }],
                },
            };
        });
    };

    const updateIvrExtension = (index, key, value) => {
        setForm((prev) => {
            const extensions = [...(prev.inboundRouting.ivrExtensions || [])];
            extensions[index] = { ...extensions[index], [key]: value };
            return {
                ...prev,
                inboundRouting: { ...prev.inboundRouting, ivrExtensions: extensions },
            };
        });
    };

    const removeIvrExtension = (index) => {
        setForm((prev) => ({
            ...prev,
            inboundRouting: {
                ...prev.inboundRouting,
                ivrExtensions: (prev.inboundRouting.ivrExtensions || []).filter((_, itemIndex) => itemIndex !== index),
            },
        }));
    };

    const handleRegeneratePromptAudio = async () => {
        if (!teamId) return;
        setLocalError('');
        setSaved(false);
        setRegenLoading(true);
        try {
            await callCenterTeamSettingsApi.regenerateIvrPromptAudio(teamId, {
                promptText: form.inboundRouting.ivrPromptScript || '',
            });
            await loadTeamSettings();
            setSaved(true);
        } catch (err) {
            setLocalError(err?.message || 'Unable to regenerate IVR prompt audio');
        } finally {
            setRegenLoading(false);
        }
    };

    const addNumberRestriction = () => {
        setForm((prev) => ({
            ...prev,
            inboundRouting: {
                ...prev.inboundRouting,
                numberRestrictions: [
                    ...(prev.inboundRouting.numberRestrictions || []),
                    { pattern: '', permissionLevel: 'all', action: 'allow', note: '' },
                ],
            },
        }));
    };

    const updateNumberRestriction = (index, key, value) => {
        setForm((prev) => {
            const restrictions = [...(prev.inboundRouting.numberRestrictions || [])];
            restrictions[index] = { ...restrictions[index], [key]: value };
            return {
                ...prev,
                inboundRouting: {
                    ...prev.inboundRouting,
                    numberRestrictions: restrictions,
                },
            };
        });
    };

    const removeNumberRestriction = (index) => {
        setForm((prev) => {
            const restrictions = [...(prev.inboundRouting.numberRestrictions || [])];
            restrictions.splice(index, 1);
            return {
                ...prev,
                inboundRouting: {
                    ...prev.inboundRouting,
                    numberRestrictions: restrictions,
                },
            };
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaved(false);
        setLocalError('');

        const ivrExtensions = form.inboundRouting.ivrExtensions || [];
        if (form.inboundRouting.strategy === 'ivr') {
            if (!ivrExtensions.length || ivrExtensions.some((extension) => !extension.digit || !extension.room)) {
                toast.error('Add at least one IVR digit and select its destination channel.');
                return;
            }

            const digits = ivrExtensions.map((extension) => String(extension.digit));
            if (new Set(digits).size !== digits.length) {
                toast.error('Each IVR extension can be assigned to only one channel.');
                return;
            }
        }

        if (form.inboundRouting.strategy === 'team_queue' && !form.inboundRouting.queueChannelId) {
            toast.error('Select a team channel to ring for incoming queue calls.');
            return;
        }

        if (form.inboundRouting.strategy === 'fallback_user' && !form.inboundRouting.fallbackUser) {
            toast.error('Select a team member to receive incoming calls.');
            return;
        }

        try {
            setTeamSettingsSaving(true);
            const response = await callCenterTeamSettingsApi.updateSettings(teamId, prepareSettingsPayload(form));
            setTeamSettings(response || null);
            setForm(normalizeSettingsForm(response));
            setSaved(true);
        } catch (error) {
            toast.error(error?.message || 'Unable to save call center settings');
        } finally {
            setTeamSettingsSaving(false);
        }
    };

    if (teamSettingsLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <div className="mt-2 text-muted">Loading call center settings...</div>
            </div>
        );
    }

    return (
        <div id="call-center-settings-root" className="call-center-settings">
            <Form onSubmit={handleSubmit} className="custom-card p-3">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h5 className="mb-1">Twilio Calling Settings</h5>
                    </div>
                    <div className="d-flex align-items-center">
                        <Switch
                            id="call-center-enabled"
                            label="Calling enabled"
                            checked={form.isCallingEnabled}
                            onChange={(event) => setForm({ ...form, isCallingEnabled: event.target.checked })}
                            size="small"
                            switchStyle={{ switchColor: 'var(--call-center-accent)' }}
                        />
                        <ConsentToolTip
                            type="info"
                            text="Turn voice calling on or off. When disabled, inbound calls are rejected and outbound manual calls are blocked."
                        />
                    </div>
                </div>

                {localError && <Alert variant="danger">{localError}</Alert>}
                {saved && <Alert variant="success">Call center settings saved.</Alert>}
                {teamSettings?.lastValidationError && (
                    <Alert variant="warning">Provider validation: {teamSettings.lastValidationError}</Alert>
                )}

                <div className="mb-4">
                    <div className="font-weight-bold mb-2">Assigned number</div>
                    {assignedNumbers.length === 0 ? (
                        <div className="call-center-settings__number call-center-settings__number--empty">
                            <div className="text-muted mb-2">No Twilio number configured for this team.</div>
                            {!hasSubAccount ? (
                                <Button
                                    variant="primary"
                                    type="button"
                                    onClick={handleSetupTeamCalling}
                                    disabled={setupLoading || !teamId}
                                >
                                    {setupLoading ? <Spinner size="sm" animation="border" className="mr-2" /> : null}
                                    {setupLoading ? 'Setting up…' : 'Set up team calling'}
                                </Button>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                        <InputGroup style={{ maxWidth: 500 }}>
                                            <Form.Control
                                                placeholder="Area code (optional)"
                                                value={areaCode}
                                                onChange={(e) =>
                                                    setAreaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))
                                                }
                                            />
                                            <InputGroup.Append>
                                                <Button
                                                    variant="outline-secondary"
                                                    type="button"
                                                    onClick={handleSearchNumbers}
                                                    disabled={numbersLoading}
                                                >
                                                    {numbersLoading ? 'Searching…' : 'Search numbers'}
                                                </Button>
                                            </InputGroup.Append>
                                        </InputGroup>
                                    </div>
                                    {numbersLoading && (
                                        <div className="text-muted small">Searching available numbers…</div>
                                    )}
                                    {!numbersLoading && availableNumbers.length === 0 && (
                                        <div className="text-muted small">
                                            No numbers found yet. Enter an area code and search.
                                        </div>
                                    )}
                                    {availableNumbers.map((num) => {
                                        const phoneNumber = getAvailableNumberValue(num);
                                        const isProvisioning = Boolean(phoneNumber) && provisioning === phoneNumber;

                                        return (
                                            <div
                                                key={phoneNumber || num.sid || num.phone_number || num.phoneNumber}
                                                className="call-center-settings__available-number d-flex justify-content-between align-items-center mb-2"
                                            >
                                                <div>
                                                    <div className="font-weight-bold">
                                                        {phoneNumber || 'Number unavailable'}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {getAvailableNumberLabel(num)}
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="success"
                                                    type="button"
                                                    onClick={() => handleProvision(num)}
                                                    disabled={!phoneNumber || isProvisioning}
                                                >
                                                    {isProvisioning ? 'Provisioning…' : 'Provision'}
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    ) : (
                        assignedNumbers.map((number) => (
                            <div
                                key={number.providerNumberId || number.e164}
                                className="call-center-settings__number d-flex justify-content-between align-items-center mb-2"
                            >
                                <div>
                                    <div className="font-weight-bold">{number.e164}</div>
                                    <div className="text-muted small">{number.label || 'Team number'}</div>
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                    {number.capabilities?.voice && <Badge variant="primary">Voice</Badge>}
                                    {number.capabilities?.sms && <Badge variant="info">SMS</Badge>}
                                    {number.isPrimary && <Badge variant="success">Primary</Badge>}
                                    {number.status && <Badge variant="secondary">{number.status}</Badge>}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="call-center-settings__accordion" data-testid="call-routing-section">
                    <OurAccordion
                        defaultShow={false}
                        titleTag={null}
                        arrowPosition="top-right"
                        title={
                            <div className="call-center-settings__accordion-title">
                                <span className="call-center-settings__accordion-icon">
                                    <BsTelephone aria-hidden="true" />
                                </span>
                                <span>
                                    <strong>Call routing</strong>
                                    <span>Choose how this team places and receives calls.</span>
                                </span>
                            </div>
                        }
                    >
                        <Row className="align-items-start">
                            <Col lg={4} md={6}>
                                <Form.Group controlId="outboundCallerId">
                                    <Form.Label className="d-flex align-items-center">
                                        Outbound caller ID
                                        <ConsentToolTip
                                            type="info"
                                            text="The assigned team number recipients see when this team calls them."
                                        />
                                    </Form.Label>
                                    <Select
                                        classNamePrefix="call-center-select"
                                        options={outboundCallerIdSelectOptions}
                                        value={
                                            outboundCallerIdSelectOptions.find(
                                                (option) => option.value === form.outboundCallerId
                                            ) || null
                                        }
                                        onChange={(option) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                outboundCallerId: option?.value || '',
                                            }))
                                        }
                                        isSearchable={false}
                                        isClearable
                                        isDisabled={!outboundCallerIdOptions.length}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                        menuPosition="fixed"
                                        styles={selectMenuStyles}
                                        placeholder={
                                            outboundCallerIdOptions.length
                                                ? 'Choose an assigned number'
                                                : 'Provision a voice number first'
                                        }
                                        aria-label="Outbound caller ID"
                                    />
                                    <Form.Text className="text-muted">
                                        Number recipients see on outbound calls.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col lg={4} md={6}>
                                <Form.Group controlId="routingStrategy">
                                    <Form.Label className="d-flex align-items-center">
                                        Inbound routing strategy
                                        <ConsentToolTip
                                            type="info"
                                            text="Choose what happens when someone calls the team's Twilio number."
                                        />
                                    </Form.Label>
                                    <Select
                                        classNamePrefix="call-center-select"
                                        options={inboundRoutingOptions}
                                        value={
                                            inboundRoutingOptions.find(
                                                (option) => option.value === form.inboundRouting.strategy
                                            ) || inboundRoutingOptions[0]
                                        }
                                        onChange={(option) => updateRouting('strategy', option?.value || 'voicemail')}
                                        isSearchable={false}
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                        menuPosition="fixed"
                                        styles={selectMenuStyles}
                                        aria-label="Inbound routing strategy"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4} md={12}>
                                <Form.Group controlId="voicemailChannelId">
                                    <Form.Label className="d-flex align-items-center">
                                        Voicemail destination channel
                                        <ConsentToolTip
                                            type="info"
                                            text="Voicemails are posted to this channel. IVR digit destinations can route them to a different channel."
                                        />
                                    </Form.Label>
                                    <ChannelSelectFilter
                                        teamIds={teamId ? [teamId] : []}
                                        assignedChannel={form.inboundRouting.voicemailChannelId}
                                        onRoomSelect={(room) => updateRouting('voicemailChannelId', room?.value || '')}
                                        placeholder="Select voicemail channel"
                                        aria-label="Voicemail destination channel"
                                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                                        menuPosition="fixed"
                                    />
                                    <Form.Text className="text-muted">
                                        Fallback destination for team voicemails.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        {form.inboundRouting.strategy === 'team_queue' && (
                            <Row>
                                <Col lg={6} md={12}>
                                    <Form.Group controlId="queueChannelId">
                                        <Form.Label className="d-flex align-items-center">
                                            Team queue channel
                                            <ConsentToolTip
                                                type="info"
                                                text="Inbound calls appear here so an available team member can answer. Unanswered calls go to voicemail."
                                            />
                                        </Form.Label>
                                        <ChannelSelectFilter
                                            teamIds={teamId ? [teamId] : []}
                                            assignedChannel={form.inboundRouting.queueChannelId}
                                            onRoomSelect={(room) => updateRouting('queueChannelId', room?.value || '')}
                                            placeholder="Select channel where agents receive calls"
                                            aria-label="Team queue channel"
                                            menuPortalTarget={
                                                typeof document !== 'undefined' ? document.body : undefined
                                            }
                                            menuPosition="fixed"
                                        />
                                        <Form.Text className="text-muted">
                                            Agents in this channel can claim the call. Unanswered calls go to the
                                            voicemail channel.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Row className="align-items-start">
                            <Col lg={8}>
                                <Form.Group controlId="voicemailGreetingText">
                                    <Form.Label className="d-flex align-items-center">
                                        Voicemail greeting
                                        <ConsentToolTip
                                            type="info"
                                            text="This message is played to the caller before the voicemail recording tone."
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={form.inboundRouting.voicemailGreetingText}
                                        onChange={(event) => updateRouting('voicemailGreetingText', event.target.value)}
                                        placeholder="Optional greeting played before the voicemail tone"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <div className="call-center-settings__switch-field">
                                    <div className="d-flex align-items-center">
                                        <Switch
                                            id="recordCalls"
                                            label="Record calls"
                                            checked
                                            disabled
                                            size="small"
                                            switchStyle={{ switchColor: 'var(--call-center-accent)' }}
                                        />
                                        <ConsentToolTip
                                            type="info"
                                            text="Live calls are always recorded and saved with call history. Recording is required and cannot be turned off. Voicemail recordings are handled separately."
                                        />
                                    </div>
                                    <small className="text-muted">Live call recording is required and always on.</small>
                                </div>
                                {form.inboundRouting.strategy === 'fallback_user' && (
                                    <Form.Group controlId="fallbackUser">
                                        <Form.Label className="d-flex align-items-center">
                                            Team member to call
                                            <ConsentToolTip
                                                type="info"
                                                text="Their saved phone number rings for inbound calls; unanswered calls go to voicemail."
                                            />
                                        </Form.Label>
                                        <Select
                                            classNamePrefix="call-center-select"
                                            options={fallbackUserOptions}
                                            value={
                                                fallbackUserOptions.find(
                                                    (option) =>
                                                        String(option.value) ===
                                                        String(form.inboundRouting.fallbackUser)
                                                ) || null
                                            }
                                            onChange={(option) => updateRouting('fallbackUser', option?.value || '')}
                                            isSearchable
                                            isClearable
                                            isLoading={teamUsersLoading}
                                            menuPortalTarget={
                                                typeof document !== 'undefined' ? document.body : undefined
                                            }
                                            menuPosition="fixed"
                                            styles={selectMenuStyles}
                                            placeholder="Select a team member with a phone number"
                                            aria-label="Fallback call recipient"
                                        />
                                        <Form.Text className="text-muted">
                                            Their phone will ring; if they do not answer, the caller is sent to
                                            voicemail.
                                        </Form.Text>
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                    </OurAccordion>
                </div>

                <div className="call-center-settings__accordion" data-testid="ivr-settings-section">
                    <OurAccordion
                        defaultShow={false}
                        titleTag={null}
                        arrowPosition="top-right"
                        title={
                            <div className="call-center-settings__accordion-title">
                                <span className="call-center-settings__accordion-icon">
                                    <BsDiagram3 aria-hidden="true" />
                                </span>
                                <span>
                                    <strong>IVR prompt</strong>
                                    <span>Configure the voice and script for digit-based routing.</span>
                                </span>
                            </div>
                        }
                    >
                        <div
                            className="d-flex flex-wrap justify-content-between align-items-start mb-3"
                            style={{ gap: 12 }}
                        >
                            <div>
                                {teamSettings?.inboundRouting?.ivrPromptAudioUrl ? (
                                    <a
                                        href={teamSettings.inboundRouting.ivrPromptAudioUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Play current prompt audio
                                    </a>
                                ) : (
                                    <span className="text-muted small">No prompt audio generated yet.</span>
                                )}
                            </div>
                            <Button
                                size="sm"
                                variant="outline-primary"
                                type="button"
                                disabled={!teamId || regenLoading}
                                onClick={handleRegeneratePromptAudio}
                            >
                                {regenLoading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="mr-2" />
                                        Generating audio…
                                    </>
                                ) : (
                                    'Generate prompt audio'
                                )}
                            </Button>
                        </div>
                        <Row>
                            <Col lg={8}>
                                <Form.Group controlId="ivrPromptScript">
                                    <Form.Label className="d-flex align-items-center">
                                        Prompt script
                                        <ConsentToolTip
                                            type="info"
                                            text="The caller hears this message before entering an IVR digit. Include spoken instructions for each configured destination."
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={form.inboundRouting.ivrPromptScript || ''}
                                        onChange={(event) => updateRouting('ivrPromptScript', event.target.value)}
                                        placeholder="For example: Press 1 for Intake or 2 for Scheduling"
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group controlId="ivrVoiceId">
                                    <Form.Label className="d-flex align-items-center">
                                        IVR voice
                                        <ConsentToolTip
                                            type="info"
                                            text="The server's configured ElevenLabs voice reads the generated IVR prompt audio."
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value="Use server default voice"
                                        readOnly
                                        aria-label="IVR voice"
                                    />
                                    <Form.Text className="text-muted">
                                        {/* The backend uses its configured ELEVENLABS_VOICE_ID. */}
                                    </Form.Text>
                                </Form.Group>
                                <Row>
                                    {/* Keep this control commented until multi-digit IVR routing is implemented end to end.
                                    <Col sm={6}>
                                        <Form.Group controlId="ivrMaxDigits">
                                            <Form.Label>Maximum digits</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={Number(form.inboundRouting.ivrMaxDigits) || 1}
                                                onChange={(event) =>
                                                    updateRouting('ivrMaxDigits', Number(event.target.value))
                                                }
                                            >
                                                {[1, 2, 3, 4].map((number) => (
                                                    <option key={number} value={number}>
                                                        {number}
                                                    </option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    */}
                                    <Col sm={12}>
                                        <Form.Group controlId="ivrTimeoutSeconds">
                                            <Form.Label className="d-flex align-items-center">
                                                Timeout (seconds)
                                                <ConsentToolTip
                                                    type="info"
                                                    text="How long Twilio waits for keypad input. If no digit is entered, the prompt repeats; after the retry limit, the caller is sent to voicemail."
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                max={30}
                                                value={Number(form.inboundRouting.ivrTimeoutSeconds) || 6}
                                                onChange={(event) =>
                                                    updateRouting('ivrTimeoutSeconds', Number(event.target.value))
                                                }
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        {form.inboundRouting.strategy === 'ivr' && (
                            <div className="call-center-settings__ivr-extensions mt-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                                    <div>
                                        <h6 className="mb-1 d-flex align-items-center">
                                            Digit destinations
                                            <ConsentToolTip
                                                type="info"
                                                text="Assign digits 1 through 9 to team channels. The call routes as soon as the caller presses the matching digit."
                                            />
                                        </h6>
                                        <small className="text-muted">
                                            Choose the team channel callers reach after pressing a digit. Digit 0 is
                                            reserved for invited callers joining an active conference.
                                        </small>
                                        <small className="d-block text-muted mt-1">
                                            Example: assign 1 to Appointments and 2 to Scheduling, then add those
                                            options to the prompt script.
                                        </small>
                                    </div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={addIvrExtension}
                                        disabled={(form.inboundRouting.ivrExtensions || []).length >= 9}
                                    >
                                        Add digit
                                    </Button>
                                </div>

                                {(form.inboundRouting.ivrExtensions || []).length === 0 ? (
                                    <div className="call-center-settings__empty-rule">
                                        Add a digit and destination channel to enable IVR routing.
                                    </div>
                                ) : (
                                    (form.inboundRouting.ivrExtensions || []).map((extension, index) => (
                                        <Row
                                            key={`${index}-${extension.digit}`}
                                            className="align-items-end call-center-settings__ivr-extension-row"
                                        >
                                            <Col sm={2} md={2}>
                                                <Form.Group controlId={`ivrExtensionDigit-${index}`}>
                                                    <Form.Label>Digit</Form.Label>
                                                    <Select
                                                        classNamePrefix="call-center-select"
                                                        options={ivrDigitOptions}
                                                        value={ivrDigitOptions.find(
                                                            (option) => option.value === String(extension.digit)
                                                        )}
                                                        onChange={(option) =>
                                                            updateIvrExtension(index, 'digit', option?.value || '')
                                                        }
                                                        isSearchable={false}
                                                        menuPortalTarget={
                                                            typeof document !== 'undefined' ? document.body : undefined
                                                        }
                                                        menuPosition="fixed"
                                                        styles={selectMenuStyles}
                                                        aria-label={`Digit for destination channel ${index + 1}`}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={6} md={5}>
                                                <Form.Group controlId={`ivrExtensionRoom-${index}`}>
                                                    <Form.Label className="d-flex align-items-center">
                                                        Destination channel
                                                        <ConsentToolTip
                                                            type="info"
                                                            text="The channel where the inbound call is offered after the caller presses this digit."
                                                        />
                                                    </Form.Label>
                                                    <ChannelSelectFilter
                                                        teamIds={teamId ? [teamId] : []}
                                                        assignedChannel={extension.room}
                                                        onRoomSelect={(room) =>
                                                            updateIvrExtension(index, 'room', room?.value || '')
                                                        }
                                                        placeholder="Select destination channel"
                                                        aria-label={`Destination channel for digit ${extension.digit}`}
                                                        menuPortalTarget={
                                                            typeof document !== 'undefined' ? document.body : undefined
                                                        }
                                                        menuPosition="fixed"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={3} md={4}>
                                                <Form.Group controlId={`ivrExtensionLabel-${index}`}>
                                                    <Form.Label className="d-flex align-items-center">
                                                        Prompt label
                                                        <ConsentToolTip
                                                            type="info"
                                                            text="A reminder for admins about this digit's purpose; it does not change the spoken prompt."
                                                        />
                                                    </Form.Label>
                                                    <Form.Control
                                                        value={extension.label || ''}
                                                        onChange={(event) =>
                                                            updateIvrExtension(index, 'label', event.target.value)
                                                        }
                                                        maxLength={120}
                                                        placeholder="Optional, e.g. Intake"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={1} className="text-right pb-3">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    aria-label={`Remove digit ${extension.digit}`}
                                                    onClick={() => removeIvrExtension(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))
                                )}
                            </div>
                        )}
                    </OurAccordion>
                </div>

                <div className="call-center-settings__accordion" data-testid="number-restrictions-section">
                    <OurAccordion
                        defaultShow={false}
                        titleTag={null}
                        arrowPosition="top-right"
                        title={
                            <div className="call-center-settings__accordion-title">
                                <span className="call-center-settings__accordion-icon">
                                    <BsShieldLock aria-hidden="true" />
                                </span>
                                <span>
                                    <strong>Number restrictions</strong>
                                    <span>Control which callers can reach the team.</span>
                                </span>
                                <Badge variant={form.inboundRouting.numberRestrictions.length ? 'info' : 'light'}>
                                    {form.inboundRouting.numberRestrictions.length}{' '}
                                    {form.inboundRouting.numberRestrictions.length === 1 ? 'rule' : 'rules'}
                                </Badge>
                            </div>
                        }
                    >
                        <div
                            className="d-flex flex-wrap justify-content-between align-items-start mb-3"
                            style={{ gap: 12 }}
                        >
                            <p className="text-muted small mb-0">
                                Match an exact E.164 number, a prefix such as <code>+1215*</code>, or <code>*</code> for
                                all callers.
                            </p>
                            <Button
                                size="sm"
                                variant="outline-primary"
                                type="button"
                                onClick={addNumberRestriction}
                                data-testid="btn-add-restriction"
                            >
                                + Add rule
                            </Button>
                        </div>
                        {form.inboundRouting.numberRestrictions.length === 0 ? (
                            <div className="call-center-settings__empty-rule">
                                No restrictions configured. All inbound callers are allowed.
                            </div>
                        ) : (
                            form.inboundRouting.numberRestrictions.map((rule, index) => (
                                <Row
                                    key={index}
                                    className="call-center-settings__restriction-row align-items-end"
                                    data-testid={`restriction-row-${index}`}
                                >
                                    <Col xl={3} md={6}>
                                        <Form.Group controlId={`restriction-pattern-${index}`}>
                                            <Form.Label className="d-flex align-items-center">
                                                Caller pattern
                                                <ConsentToolTip
                                                    type="info"
                                                    text="Match an exact E.164 number, a prefix ending in *, or * for every caller."
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="+1215*"
                                                value={rule.pattern}
                                                onChange={(event) =>
                                                    updateNumberRestriction(index, 'pattern', event.target.value.trim())
                                                }
                                                data-testid={`restriction-pattern-${index}`}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={2} md={6}>
                                        <Form.Group controlId={`restriction-action-${index}`}>
                                            <Form.Label className="d-flex align-items-center">
                                                Action
                                                <ConsentToolTip
                                                    type="info"
                                                    text="Allow a matching caller, deny the call, or send it directly to voicemail."
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={rule.action}
                                                onChange={(event) =>
                                                    updateNumberRestriction(index, 'action', event.target.value)
                                                }
                                                data-testid={`restriction-action-${index}`}
                                            >
                                                <option value="allow">Allow</option>
                                                <option value="deny">Deny</option>
                                                <option value="voicemail">Send to voicemail</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col xl={2} md={6}>
                                        <Form.Group controlId={`restriction-permission-${index}`}>
                                            <Form.Label className="d-flex align-items-center">
                                                Who may answer
                                                <ConsentToolTip
                                                    type="info"
                                                    text="Sets the minimum team role allowed to claim calls from matching numbers. This applies only when the action is Allow."
                                                />
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={rule.permissionLevel}
                                                onChange={(event) =>
                                                    updateNumberRestriction(
                                                        index,
                                                        'permissionLevel',
                                                        event.target.value
                                                    )
                                                }
                                                disabled={rule.action !== 'allow'}
                                                data-testid={`restriction-permission-${index}`}
                                            >
                                                <option value="all">All users</option>
                                                <option value="teamManager">Team Manager+</option>
                                                {/* Reserved for when these roles are supported by the call-center permission model.
                                                <option value="admin">Admin+</option>
                                                <option value="agent">Agent+</option>
                                                */}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col xl={4} md={6}>
                                        <Form.Group controlId={`restriction-note-${index}`}>
                                            <Form.Label>
                                                Note <span className="text-muted font-weight-normal">(optional)</span>
                                            </Form.Label>
                                            <Form.Control
                                                placeholder="Why is this rule needed?"
                                                value={rule.note || ''}
                                                onChange={(event) =>
                                                    updateNumberRestriction(index, 'note', event.target.value)
                                                }
                                                data-testid={`restriction-note-${index}`}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xl={1} md={12} className="text-md-right">
                                        <Form.Group>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                type="button"
                                                className="mb-3"
                                                onClick={() => removeNumberRestriction(index)}
                                                data-testid={`restriction-remove-${index}`}
                                            >
                                                Remove
                                            </Button>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            ))
                        )}
                    </OurAccordion>
                </div>

                <div className="d-flex justify-content-end">
                    <Button type="submit" variant="primary" disabled={teamSettingsSaving || !teamId}>
                        {teamSettingsSaving ? (
                            <>
                                <Spinner size="sm" animation="border" className="mr-2" />
                                Saving...
                            </>
                        ) : (
                            'Save Settings'
                        )}
                    </Button>
                </div>
            </Form>
        </div>
    );
};

const SettingsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <SettingsTabComponent {...props} />
    </ComponentErrorBoundary>
);

export default SettingsTab;
